import type React from "react";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Paper,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  type SelectChangeEvent,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { loginStart, loginSuccess, loginFailure } from "../store/features/userSlice";
import type { RootState } from "../store";
import type translations from "../translations/translations.json";
import { loginApi } from "../api/usersApi";
import { getClientTimezone, getDefaultLandingPath } from "../types/user";
import { getErrorMessage } from "../utils/errorHandling";
import { rawColor } from "../constants/colors";
import { TestId } from "../constants/testIds";

type TranslationKeys = keyof typeof translations.en;

interface LoginFormData {
  email: string;
  password: string;
}

const StyledPaper = styled(Paper)`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 8px;
  box-shadow: ${rawColor.shadow.light.medium};
`;

const StyledForm = styled("form")`
  width: 100%;
  margin-top: 1rem;
`;

const StyledTextField = styled(TextField)`
  margin-bottom: 1rem;
`;

const StyledSelect = styled(Select<string>)`
  font-size: 0.875rem;
  color: ${rawColor.text.secondary.light};
  .MuiOutlinedInput-notchedOutline {
    border: none;
  }
  &:hover .MuiOutlinedInput-notchedOutline {
    border: none;
  }
  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border: none;
  }
`;

const LoginPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error } = useSelector((state: RootState) => state.user);
  const { t, i18n } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    i18n.changeLanguage(event.target.value);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(loginStart());

    try {
      const response = await loginApi({
        id: formData.email,
        password: formData.password,
        timezone: getClientTimezone(),
      });
      const { token, user } = response.data;

      dispatch(loginSuccess({ token, user }));

      const from = (location.state as { from?: { pathname?: string; search?: string } } | null)
        ?.from;
      if (from?.pathname) {
        navigate(`${from.pathname}${from.search ?? ""}`, { replace: true });
      } else {
        navigate(getDefaultLandingPath(user), { replace: true });
      }
    } catch (err: unknown) {
      dispatch(loginFailure(getErrorMessage(err, t, "login")));
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <StyledPaper elevation={3}>
          <Typography component="h1" variant="h5">
            {t("Sign in" as TranslationKeys)}
          </Typography>
          {error && (
            <Alert
              severity="error"
              sx={{ mt: 2, width: "100%" }}
              data-testid={TestId.LOGIN.ERROR_MESSAGE}
            >
              {error}
            </Alert>
          )}
          <StyledForm onSubmit={handleSubmit}>
            <StyledTextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t("Username" as TranslationKeys)}
              name="email"
              autoComplete="username"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              data-testid={TestId.LOGIN.EMAIL_INPUT}
            />
            <StyledTextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t("Password" as TranslationKeys)}
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              data-testid={TestId.LOGIN.PASSWORD_INPUT}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
              data-testid={TestId.LOGIN.SUBMIT_BUTTON}
            >
              {loading ? <CircularProgress size={24} /> : t("Sign in" as TranslationKeys)}
            </Button>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <FormControl size="small">
                <StyledSelect value={i18n.language} onChange={handleLanguageChange} size="small">
                  <MenuItem value="en">{t("English" as TranslationKeys)}</MenuItem>
                  <MenuItem value="id">{t("Indonesia" as TranslationKeys)}</MenuItem>
                </StyledSelect>
              </FormControl>
            </Box>
          </StyledForm>
        </StyledPaper>
      </Box>
    </Container>
  );
};

export default LoginPage;
